// Pure EHR identity data with no imports: kept dependency-free so it can be
// imported from anywhere (apps, tests, module scope) without pulling in the
// provider/context graph or risking circular-initialization issues.

export const ehrs = [
  'athena',
  'canvas',
  'elation',
  'healthie',
  'salesforce',
  'ecw',
  'epic',
  'oncoemr',
  'medplum',
] as const;
export type EHR = (typeof ehrs)[number];

export const ehrDisplayNames: Record<EHR, string> = {
  athena: 'athenahealth',
  canvas: 'Canvas',
  ecw: 'eClinicalWorks',
  elation: 'Elation',
  healthie: 'Healthie',
  salesforce: 'Salesforce',
  epic: 'Epic',
  oncoemr: 'OncoEMR',
  medplum: 'Medplum',
};

// Salesforce patients sync under two identifier systems, depending on which
// Salesforce object models the patient: Person Accounts (record Id key prefix 001)
// vs Contacts (key prefix 003). See the official Salesforce Entity Key Prefix
// Decoder: https://help.salesforce.com/s/articleView?id=000385203&language=en_US&type=1
export const SALESFORCE_ACCOUNT_SYSTEM_URL = 'https://salesforce.com/account-id';
export const SALESFORCE_CONTACT_SYSTEM_URL = 'https://salesforce.com/contact-id';

// Each EHR's patient identifier systems. Most EHRs have exactly one; Salesforce
// has one per source object (Person Account vs Contact), and the correct one must
// be derived from context (see apps/zap-iframe salesforce-record-type) rather than assumed.
export const ehrSystemURLs: Record<EHR, [string, ...Array<string>]> = {
  athena: ['https://fhir.athena.io/sid/ah-patient'],
  canvas: ['https://canvasmedical.com/patient-id'],
  ecw: ['https://www.eclinicalworks.com/patient-id'],
  elation: ['https://elationemr.com/patient-id'],
  healthie: ['https://www.gethealthie.com'],
  salesforce: [SALESFORCE_ACCOUNT_SYSTEM_URL, SALESFORCE_CONTACT_SYSTEM_URL],
  epic: ['http://open.epic.com/FHIR/StructureDefinition/patient-fhir-id'],
  oncoemr: ['https://fhir.prod.flatiron.io/patient-id'],
  medplum: ['https://api.medplum.com/patient-id'],
};

export const getSoleEhrSystemURL = (ehr: EHR): string => {
  // Annotated as possibly-undefined: callers may reach this with a runtime cast
  // from an unvalidated string, in which case the lookup misses.
  const systemURLs: ReadonlyArray<string> | undefined = ehrSystemURLs[ehr];
  if (!systemURLs || systemURLs.length === 0) {
    throw new Error(`Unknown EHR "${ehr}"`);
  }
  if (systemURLs.length > 1) {
    throw new Error(
      `EHR "${ehr}" has multiple patient identifier systems; derive the correct one from context instead of assuming a default`,
    );
  }
  return systemURLs[0];
};
